// Copyright 2015-2020 MathWorks, Inc

/**
 * This class provides the focus management for an application.  The default Dojo/Dijit focus management tends to
 * give focus to any element that is clicked on.  Single page applications tend to have many elements that should not
 * get focus in response to a simple click.  Examples include widgets on the toolstrip, pane dividers and tab container
 * tabs.  This class provides precise control over the behavior of focus in response to clicks by allowing clients to
 * specify behavior at a dom-element level.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/query",
    "dijit/popup",
    "dijit/registry",
    "mw-dom-utils/domUtils"
], function (declare, lang, dom, domAttr, domClass, query, popup, registry, domUtils) {

    var shouldRefuseFocus = function (node) {
        while (node) {
            //Second condition for splitter
            if ((node.getAttribute && node.getAttribute('data-focus') === 'refuse') ||
                domAttr.get(node, "data-focus") === 'refuse') {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    };

    var shouldAcceptTemporaryFocus = function (node) {
        if ((node.getAttribute && node.getAttribute('data-focus') === 'temporary') ||
            domAttr.get(node, "data-focus") === 'temporary') {
            return true;
        }
        return false;
    };

    var isChildOfShouldAcceptTemporaryFocus = function (node) {
        while (node) {
            // g2141555: Popups are not direct descendants of the invoking widget in the DOM. So, find out the correct
            // parent/invoking widget of the popup before checking for the "data-focus" attribute.
            if (node && node.classList && node.classList.contains("dijitPopup")) {
                // All the widgets that open a mw-menu should have "mwHasMenuOpened" class. The color picker buttons in
                // the editor panels use dijit widgets for which we need to use "dijitDropDownButtonOpened" class.
                const popupParentNode = domUtils.findParentNode(node, "mwHasMenuOpened")
                    || domUtils.findParentNode(node, "dijitDropDownButtonOpened");
                if (popupParentNode) {
                    node = popupParentNode;
                } else {
                    return false;
                }
            }
            if (domAttr.get(node, "data-focus") === 'temporary') {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    };


    // Check the data- tags to see if the node should get focus
    var shouldAcceptFocus = function (node) {
        // Special case for figure menu bar because it doesn't currently set the accept data-focus =accept
        // TODO: This should be eliminated in favor of simply tagging the menu bar
        var menuBars = query("[data-tag='motwToolstrip.figureTab.menuAndToolbars.menubarWrapper']");
        var menuBar = menuBars.length > 0 ? menuBars[0] : null;
        while (node) {
            if (domAttr.get(node, "data-focus") === 'accept' ||
                (menuBar && dom.isDescendant(node, menuBar))) {
                return true;
            }
            if (domAttr.get(node, "data-focus") === 'refuse') {
                return false;
            }
            node = node.parentNode;
        }
        return true;
    };

    // See if the given node is contained within a popup
    var descendsFromPopup = function (node) {
        while (node) {
            if (domClass.contains(node, "dijitPopup")) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    };
    
    // _CssStateMixin will only remove all classes if a blur event is fired from a node
    // These classes need to be removed in cases such as when the window is blurred by an iFrame
    var emitBlurEventFromNode = function (node) {
          var event; // The custom event that will be created

          if (window.FocusEvent) {
              event = new FocusEvent('blur');
              node.dispatchEvent(event);
          } else if (document.createEvent) {
              event = document.createEvent("HTMLEvents");
              event.initEvent("blur", true, true);
              node.dispatchEvent(event);
          } else {
              event = document.createEventObject();
              event.eventType = "blur";
              element.fireEvent("on" + event.eventType, event);
          }
    };

    var isDialogOpen = function () {
        // TODO: Do not rely on classes for the dialog check instead use an API when it is available - g1724372
        return document.body.querySelector(".mwModalDialog") || document.body.querySelector(".dijitDialog")
            || document.body.querySelector(".mwModelessDialog");
    };

    var currentFocusedElement = null;
    // Temporary workaround for g2036979: have body as a default permanentFocusedElement
    var permanentFocusedElement = document.body;
    var savedFocusedElement = null;
    var lastNonRefuseElement = null;
    var focusedElementInIframe = null;

    var doNotRestoreFocusOnWindowFocus = false;

    /**
     * @class
     */
    var FocusManager = declare(null, {

        _restoreFocusToLastNonRefuseElementHandler: function () {
            FocusManager.restoreToLastNonRefuseElement();
        },

        _restoreFocusHandler: function () {
            FocusManager.restoreFocus();
        },

        _blurHandler: function (e) {
            if (!savedFocusedElement) {
                if (e.target === window) {
                    FocusManager._windowBlurred = true;
                }
                setTimeout(function() {
                    if (document.activeElement.tagName === "IFRAME") {
                        if ([undefined, null, window, document, document.body].indexOf(document.activeElement.parentElement) < 0) {
                            if (permanentFocusedElement) {
                                emitBlurEventFromNode(permanentFocusedElement);
                            }
                            
                            if (shouldAcceptFocus(document.activeElement.parentElement)) {
                                savedFocusedElement = document.activeElement;

                                if (!isChildOfShouldAcceptTemporaryFocus(savedFocusedElement) && shouldAcceptFocus(savedFocusedElement)) {
                                    permanentFocusedElement = savedFocusedElement;
                                }

                                if (savedFocusedElement.contentDocument && savedFocusedElement.contentDocument.activeElement) {
                                    focusedElementInIframe = savedFocusedElement.contentDocument.activeElement;
                                } else {
                                    focusedElementInIframe = null;
                                }

                                // Focus the parentElement to establish the focus history, and set classes
                                focusElement(savedFocusedElement.parentElement);
                                
                                // Refocus the iFrame in order to ensure proper expected focus behavior
                                setTimeout(function () {
                                    if (focusedElementInIframe) {
                                        focusElement(focusedElementInIframe);
                                    }
                                }, 0);
                            }
                        }
                        FocusManager._iFrameClicked = true;
                    }
                }, 0);
            }
        },

        _mouseDownHandler: function (e) {
            //Clicking on menuitems and checkbox menuitems
            if (isChildOfShouldAcceptTemporaryFocus(e.target)) {
                return;
            } else if (shouldAcceptFocus(e.target)) {
                if (!savedFocusedElement && (e.target.children.length === 1) && (e.target.children[0].tagName === "IFRAME")) {
                    permanentFocusedElement = e.target.children[0];
                    savedFocusedElement = permanentFocusedElement;
                    focusElement(permanentFocusedElement);
                }
//                permanentFocusedElement = e.target;
                //If node accepts focus, change permanent focus element
                //Splitter does not take focus but should be refused
            } else if (shouldRefuseFocus(e.target)) {
                //Need to check in viewport for gallery dropdown button as dropdown overlays the button
                setTimeout(function () {
                    //Check for click on editor and click on gallery, popup is already opened
                    //Click on empty area of Toolstrip=> no popup
                    if (popup._stack.length === 0) {
                        //IE-> double click on Toolstrip does not have focused border
                        if( registry.getEnclosingWidget(permanentFocusedElement)) {
                            registry.getEnclosingWidget(permanentFocusedElement).set('focused', true);
                        }
                        //For initial loading-> Geck G1377846
                        if(permanentFocusedElement && !savedFocusedElement) {
                            // g1813904: Permanent focused element should not be focused when a dialog is open
                            if (!isDialogOpen()) {
                                focusElement(permanentFocusedElement);
                            }
                        }
                    }

                }, 0);
                //UICONTAINER click on new type1 and switch between two documents
            } else {
                //For context menu or menu click outside, do not focus on clicked element
                if (popup._stack.length > 0) {
                    setTimeout(function () {
                        if(permanentFocusedElement && !savedFocusedElement) {
                            focusElement(permanentFocusedElement);
                        }
                    }, 0)
                } else {
                    permanentFocusedElement = e.target;
                }
            }
        },

        _focusHandler: function (e) {
            //fOR DOC BROWSER ON ie 11 GECK
            if(e.target.tagName==='IFRAME') {
                return;
            }

            // g1704517: Firefox fires two events on capture phase, when there is a 'focus' listener on the window.
            // Ignore the one where document object is the event target.
            if (e.target === document) {
                return;
            }

            // g1762151: Do not restore focus when the window blur occurs due to a click on the
            // iframe. Setting FocusManager._windowBlurred to false will help to avoid restoring
            // focus.
            if (FocusManager._iFrameClicked === true) {
                FocusManager._windowBlurred = false;
                FocusManager._iFrameClicked = false;
            } else if (FocusManager._windowBlurred === true) { //Click outside and click back
                FocusManager._windowBlurred = false;

                if (!doNotRestoreFocusOnWindowFocus) {
                    FocusManager.restoreFocus(true);
                }

                return;
            }

            // e.target may be Window, or an iFrames Window
            if (!(e.target instanceof Element)) {
                return;
            }

            currentFocusedElement = e.target;

            //Track last non refuse element - > TextField press Enter usecase
            if (!shouldRefuseFocus(currentFocusedElement)) {
                if (!isChildOfShouldAcceptTemporaryFocus(currentFocusedElement)) {
                    lastNonRefuseElement = e.target;
                }
            }
            if (shouldAcceptTemporaryFocus(currentFocusedElement)) {
//                    previousPermanentFocusedElement = permanentFocusedElement;
//                    permanentFocusedElement = e.target;
            } else if (isChildOfShouldAcceptTemporaryFocus(currentFocusedElement)) {
                //do not do anything
            } else if (shouldAcceptFocus(currentFocusedElement)) {
                //If node accepts focus, change permanent focus element
                if(e.target!==document.body) {
                    if (savedFocusedElement) {// Since iFrames are not always focus targets, we need to check if the parent of an iFrame is being focused instead
                        if ([].slice.call(e.target.children).indexOf(savedFocusedElement) > -1 && permanentFocusedElement) {
                            focusElement(permanentFocusedElement);
                            return;
                        }
                        savedFocusedElement = null;
                    }
                    permanentFocusedElement = e.target;
                }
            } else if (shouldRefuseFocus(currentFocusedElement)) {
                //Hidden in command window and editor
                //Clicking on textfield with placeholder and Tab does not work

//                    else if (!isElementInViewport(permanentFocusedElement)) {
//                        console.log('focusing body');
//                        //TODO: expose a property which takes in an element to focus by default?
//                        //document.body.focus() again sets focus on the current active Element inside body and does not take away focus
//                        document.activeElement.blur();
//                    }
                if (permanentFocusedElement) {
                    //To get focused border
                    setTimeout(function () {
			//Go behind to focus, other wise focused state does not trigger
                        //For example, the blue border on focus state
                        //:focus selector also does not trigger
                        //click on dropdown button should not return focus
                        // The second clause prevents click on tooltip from stealing focus (see geck 1529663).
                    	if ((popup._stack.length === 0 && permanentFocusedElement) || descendsFromPopup(e.target)) {
                            if (savedFocusedElement) {
                                savedFocusedElement = null;
                            }

                            // g1813904: Permanent focused element should not be focused when a dialog is open
                            if (!isDialogOpen()) {
                                focusElement(permanentFocusedElement);
                            }
                        }
                    }, 0);
                } else {
                    //document.body.focus() again sets focus on the current active Element inside body and does not take away focus
                    document.activeElement.blur();
                }
                //Standard elements that do not have any tags -> accept focus
            } else {
                if(e.target!==document.body) {
                    if (savedFocusedElement) {
                        savedFocusedElement = null;
                    }
                    permanentFocusedElement = e.target;
                }
            }
        },

        _closeHandler: function (e) {
            // If the close was emitted by a context menu, return focus to the previous focus node
            if (e.target.contains(document.activeElement) &&
                e.target.classList.contains("mwContextMenu") &&
                e.mwFocusData &&
                e.mwFocusData.previousFocusNode) {
                focusElement(e.mwFocusData.previousFocusNode);
                return;
            }
            if (e.target.contains(document.activeElement) && permanentFocusedElement) {
                // If the menu close request happened for a widget when it resides in the QAB overflow popup, then we do
                // not want to force close all the popups as it will close the QAB overflow popup.
                var qabOverflowPopupNode = document.querySelector('[data-tag="qabOverflowPopup"]');
                if (qabOverflowPopupNode) {
                    var widgetId;
                    if (document.activeElement.parentElement) {
                        widgetId = document.activeElement.parentElement.getAttribute('dijitpopupparent');
                        if (widgetId) {
                            var widget = registry.byId(widgetId);
                            if (widget && widget.domNode && qabOverflowPopupNode.contains(widget.domNode)) {
                                // Just return to avoid force closing all the popups from the subsequent lines of code.
                                return;
                            }
                        }
                    }
                }

                // force close all popups before focusing permanent element
                popup.close();
                focusElement(permanentFocusedElement);
            }
        },

        constructor: function (toolstrip, disableRestoreFocusOnWindowFocus) {
            FocusManager._instantiated = true;
            if (toolstrip) {
                FocusManager.refuseFocus(toolstrip.domNode);
            }

            // g1814496: Adding a boolean flag to allow opting out the focus restoring logic that
            // typically happens after a window blur and window focus happen. This is intended to be
            // used as a workaround for g1814496.
            if (disableRestoreFocusOnWindowFocus) {
                doNotRestoreFocusOnWindowFocus = true;
            }

            document.addEventListener('restorefocustolastnonrefuseelement',
                this._restoreFocusToLastNonRefuseElementHandler);


            document.addEventListener('restorefocus', this._restoreFocusHandler);

            //Do not listen on capture as you get multiple blur events
            window.addEventListener('blur', this._blurHandler);
            document.body.addEventListener('blur', this._blurHandler);

            // Listen in capturing phase as focusevent does not bubble
            //If you attach a focus event on the domnode, it would fire first before it hits document's focus
            window.addEventListener('focus', this._focusHandler, true);

            document.addEventListener('mousedown', this._mouseDownHandler, true);

            document.addEventListener('close', this._closeHandler, true);

            document.body.classList.add("mwHasFocusManager");
        },

        destroy: function () {

            document.removeEventListener('restorefocustolastnonrefuseelement',
                this._restoreFocusToLastNonRefuseElementHandler);

            document.removeEventListener('restorefocus', this._restoreFocusHandler);

            //Do not listen on capture as you get multiple blur events
            window.removeEventListener('blur', this._blurHandler);
            document.body.removeEventListener('blur', this._blurHandler);

            // Listen in capturing phase as focusevent does not bubble
            //If you attach a focus event on the domnode, it would fire first before it hits document's focus
            window.removeEventListener('focus', this._focusHandler, true);

            document.removeEventListener('mousedown', this._mouseDownHandler, true);

            document.body.classList.remove("mwHasFocusManager");
        }
    });

    /**
     * Tags the given node to indicate that neither it nor its descendants should be given focus, with the
     * exception of descendants that have been tagged to accept focus via the acceptFocus method below.
     */
    FocusManager.refuseFocus = function (domNode) {
        domNode.setAttribute("data-focus", "refuse");
    };

    /**
     * Tags the given node to indicate that it should accept focus even if one of its ancestors is tagged to refuse
     * focus.
     */
    FocusManager.acceptFocus = function (domNode) {
        domNode.setAttribute("data-focus", "accept");
    };

    FocusManager.temporaryFocus = function (domNode) {
        domNode.setAttribute("data-focus", "temporary");
    };

    /**
     * Tags the given node to indicate that the scrolling should be prevented when it gets focused.
     */
    FocusManager.preventScroll = function (domNode) {
        domNode.setAttribute('prevent-scroll', true);
    };

    /**
     * Ensures that the given node does not prevent scrolling when it gets focused.
     */
    FocusManager.allowScroll = function (domNode) {
        domNode.removeAttribute('prevent-scroll');
    };

    // Call the native focus API depending on whether the domNode is configured to prevent scroll or not.
    var focusElement = function (element) {
        if (element.matches('[prevent-scroll=true], [prevent-scroll=true] *')) {
            element.focus({ preventScroll: true });
        } else {
            element.focus();
        }
    };

    FocusManager.restoreFocus = function (doNotUseTimeout) {
        if (doNotUseTimeout) {
            if (permanentFocusedElement) {
                focusElement(permanentFocusedElement);
            }
        } else {
            setTimeout(function () {
                if (permanentFocusedElement) {
                    focusElement(permanentFocusedElement);
                }
            }, 0);
        }
    };

    FocusManager.restoreToLastNonRefuseElement = function () {
        setTimeout(lang.hitch(this, function () {
            if (lastNonRefuseElement) {
                focusElement(lastNonRefuseElement);
            }
        }, 0));

    };

    FocusManager.isAvailable = function () {
        //Return boolean value true/false to indicate if widget needs to act using focus manager or not
        if (FocusManager._instantiated === true) {
            return true;
        } else {
            return false;
        }
    };

    FocusManager.getPermanentFocusedElement = function () {
        return permanentFocusedElement;
    };

    return FocusManager;
});
